import React, { useState } from 'react';
import {
  Box,
  Badge,
  Button,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import {
  EventFilter,
  EventCategory,
  IndustryType,
  CompanyScaleType,
  CATEGORY_LABELS,
  INDUSTRY_LABELS,
  COMPANY_SCALE_LABELS,
} from '../../types/event';

interface EventFilterBarProps {
  filter: EventFilter;
  onChange: (filter: EventFilter) => void;
  showAdminFilter?: boolean;
  showTagFilter?: boolean;
  showCompanyFilter?: boolean;
}

export const EventFilterBar: React.FC<EventFilterBarProps> = ({
  filter,
  onChange,
  showAdminFilter = false,
  showTagFilter = false,
  showCompanyFilter = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleChange = (key: keyof EventFilter) => (e: any) => {
    const value = e.target.value as string;
    onChange({ ...filter, [key]: value || undefined });
  };

  const handleReset = () => {
    onChange({});
    setDrawerOpen(false);
  };

  const activeFilterCount = [
    filter.industry,
    filter.company_scale,
    filter.category,
    filter.creator_type,
    filter.tag_slug,
    filter.own_only,
  ].filter(Boolean).length;

  const hasFilter = activeFilterCount > 0;

  // ── モバイル：ドロワー方式 ──────────────────────────────
  if (isMobile) {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              size="small"
              startIcon={<TuneIcon />}
              onClick={() => setDrawerOpen(true)}
            >
              絞り込み・並び替え
            </Button>
          </Badge>
        </Box>

        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { borderRadius: '16px 16px 0 0', px: 2, pb: 4, pt: 2 } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={700}>絞り込み・並び替え</Typography>
            <Button onClick={() => setDrawerOpen(false)} sx={{ minWidth: 0, p: 0.5 }}>
              <CloseIcon />
            </Button>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>業種</InputLabel>
              <Select value={filter.industry ?? ''} label="業種" onChange={handleChange('industry')}>
                <MenuItem value="">すべて</MenuItem>
                {(Object.keys(INDUSTRY_LABELS) as IndustryType[]).map((key) => (
                  <MenuItem key={key} value={key}>{INDUSTRY_LABELS[key]}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>企業規模</InputLabel>
              <Select value={filter.company_scale ?? ''} label="企業規模" onChange={handleChange('company_scale')}>
                <MenuItem value="">すべて</MenuItem>
                {(Object.keys(COMPANY_SCALE_LABELS) as CompanyScaleType[]).map((key) => (
                  <MenuItem key={key} value={key}>{COMPANY_SCALE_LABELS[key]}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>イベント種別</InputLabel>
              <Select value={filter.category ?? ''} label="イベント種別" onChange={handleChange('category')}>
                <MenuItem value="">すべて</MenuItem>
                {(Object.keys(CATEGORY_LABELS) as EventCategory[]).map((key) => (
                  <MenuItem key={key} value={key}>{CATEGORY_LABELS[key]}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {showTagFilter && (
              <FormControl fullWidth size="small">
                <InputLabel>タグ</InputLabel>
                <Select value={filter.tag_slug ?? ''} label="タグ" onChange={handleChange('tag_slug')}>
                  <MenuItem value="">すべて</MenuItem>
                  <MenuItem value="entrepreneurship">起業向け</MenuItem>
                  <MenuItem value="job_hunting">就活向け</MenuItem>
                  <MenuItem value="learning">学習向け</MenuItem>
                </Select>
              </FormControl>
            )}

            {showAdminFilter && (
              <FormControl fullWidth size="small">
                <InputLabel>作成者</InputLabel>
                <Select value={filter.creator_type ?? ''} label="作成者" onChange={handleChange('creator_type')}>
                  <MenuItem value="">すべて</MenuItem>
                  <MenuItem value="admin">管理者が作成</MenuItem>
                  <MenuItem value="company">企業が作成</MenuItem>
                </Select>
              </FormControl>
            )}

            {showCompanyFilter && (
              <FormControl fullWidth size="small">
                <InputLabel>表示対象</InputLabel>
                <Select value={filter.own_only ?? ''} label="表示対象" onChange={handleChange('own_only')}>
                  <MenuItem value="">すべて</MenuItem>
                  <MenuItem value="own">自社のイベントのみ</MenuItem>
                  <MenuItem value="other">他社のイベントのみ</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
            <Button variant="outlined" fullWidth onClick={handleReset}>
              リセット
            </Button>
            <Button variant="contained" fullWidth onClick={() => setDrawerOpen(false)}>
              適用する
            </Button>
          </Box>
        </Drawer>
      </>
    );
  }

  // ── PC：インライン横並び方式 ────────────────────────────
  return (
    <Box mb={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>業種</InputLabel>
          <Select value={filter.industry ?? ''} label="業種" onChange={handleChange('industry')}>
            <MenuItem value="">すべて</MenuItem>
            {(Object.keys(INDUSTRY_LABELS) as IndustryType[]).map((key) => (
              <MenuItem key={key} value={key}>{INDUSTRY_LABELS[key]}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>企業規模</InputLabel>
          <Select value={filter.company_scale ?? ''} label="企業規模" onChange={handleChange('company_scale')}>
            <MenuItem value="">すべて</MenuItem>
            {(Object.keys(COMPANY_SCALE_LABELS) as CompanyScaleType[]).map((key) => (
              <MenuItem key={key} value={key}>{COMPANY_SCALE_LABELS[key]}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>イベント種別</InputLabel>
          <Select value={filter.category ?? ''} label="イベント種別" onChange={handleChange('category')}>
            <MenuItem value="">すべて</MenuItem>
            {(Object.keys(CATEGORY_LABELS) as EventCategory[]).map((key) => (
              <MenuItem key={key} value={key}>{CATEGORY_LABELS[key]}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {showTagFilter && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>タグ</InputLabel>
            <Select value={filter.tag_slug ?? ''} label="タグ" onChange={handleChange('tag_slug')}>
              <MenuItem value="">すべて</MenuItem>
              <MenuItem value="entrepreneurship">起業向け</MenuItem>
              <MenuItem value="job_hunting">就活向け</MenuItem>
              <MenuItem value="learning">学習向け</MenuItem>
            </Select>
          </FormControl>
        )}

        {showAdminFilter && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>作成者</InputLabel>
            <Select value={filter.creator_type ?? ''} label="作成者" onChange={handleChange('creator_type')}>
              <MenuItem value="">すべて</MenuItem>
              <MenuItem value="admin">管理者が作成</MenuItem>
              <MenuItem value="company">企業が作成</MenuItem>
            </Select>
          </FormControl>
        )}

        {showCompanyFilter && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>表示対象</InputLabel>
            <Select value={filter.own_only ?? ''} label="表示対象" onChange={handleChange('own_only')}>
              <MenuItem value="">すべて</MenuItem>
              <MenuItem value="own">自社のイベントのみ</MenuItem>
              <MenuItem value="other">他社のイベントのみ</MenuItem>
            </Select>
          </FormControl>
        )}

        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<FilterListOffIcon />}
          onClick={() => onChange({})}
          disabled={!hasFilter}
          sx={{
            whiteSpace: 'nowrap',
            color: hasFilter ? 'text.primary' : 'text.disabled',
            borderColor: hasFilter ? 'text.secondary' : 'divider',
          }}
        >
          フィルターをリセット
        </Button>
      </Stack>
    </Box>
  );
};
